import React, { useEffect, useState } from "react";
import { api } from "../lib/api";
import { MessageSquarePlus, Pencil, Trash2, Megaphone } from "lucide-react";
import { toast } from "sonner";
import UpdateDialog from "./UpdateDialog";

function formatDate(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleDateString("en-US", {
      month: "short", day: "numeric", year: "numeric",
    });
  } catch { return ""; }
}

function UpdateItem({ update, readOnly, onEdit, onDelete }) {
  return (
    <div className="card-soft p-6" data-testid={`update-${update.id}`}>
      <div className="flex items-start justify-between gap-4">
        <div className="flex-1 min-w-0">
          <h3 className="font-outfit text-xl font-medium text-ink">{update.title}</h3>
          <p className="font-manrope text-xs text-ink-soft mt-1">
            {update.author_name ? `${update.author_name} · ` : ""}{formatDate(update.created_at)}
            {update.updated_at && update.updated_at !== update.created_at && " · edited"}
          </p>
        </div>
        {!readOnly && (
          <div className="flex gap-1 shrink-0">
            <button onClick={onEdit} className="p-2 rounded-full hover:bg-brand-soft" data-testid={`update-edit-${update.id}`}>
              <Pencil className="h-4 w-4 text-ink-soft" />
            </button>
            <button onClick={onDelete} className="p-2 rounded-full hover:bg-red-50" data-testid={`update-delete-${update.id}`}>
              <Trash2 className="h-4 w-4 text-red-600" />
            </button>
          </div>
        )}
      </div>
      <p className="mt-4 font-manrope text-ink leading-relaxed whitespace-pre-wrap">{update.body}</p>
    </div>
  );
}

function UpdatesEmptyState({ readOnly }) {
  return (
    <div className="mt-6 card-soft p-8 text-center" data-testid="updates-empty">
      <Megaphone className="h-8 w-8 text-brand mx-auto mb-3 opacity-40" />
      <p className="font-manrope text-sm text-ink-soft">
        {readOnly
          ? "No updates yet. Check back soon."
          : "No updates yet. Share how recovery is going — your people want to know."}
      </p>
    </div>
  );
}

function UpdatesHeader({ readOnly, onNew }) {
  return (
    <div className="flex items-center justify-between flex-wrap gap-3">
      <div>
        <h2 className="font-outfit text-2xl font-medium text-ink inline-flex items-center gap-2">
          <Megaphone className="h-5 w-5 text-brand" /> {readOnly ? "Updates" : "Your updates"}
        </h2>
        <p className="font-manrope text-sm text-ink-soft mt-1">
          {readOnly
            ? "Recovery notes and news from the registry owner."
            : "Post updates to keep your supporters in the loop — like GoFundMe."}
        </p>
      </div>
      {!readOnly && (
        <button onClick={onNew} className="btn-primary inline-flex items-center gap-2" data-testid="updates-new-btn">
          <MessageSquarePlus className="h-4 w-4" /> Post update
        </button>
      )}
    </div>
  );
}

export default function RegistryUpdates({ readOnly = false, slug = null }) {
  const [updates, setUpdates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = async () => {
    setLoading(true);
    try {
      const url = readOnly ? `/registry/public/${slug}/updates` : "/registry/me/updates";
      const { data } = await api.get(url);
      setUpdates(data);
    } catch (e) {
      toast.error("Couldn't load updates.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readOnly, slug]);

  const remove = async (id) => {
    if (!window.confirm("Delete this update?")) return;
    try {
      await api.delete(`/registry/me/updates/${id}`);
      setUpdates(updates.filter((u) => u.id !== id));
      toast.success("Deleted.");
    } catch (e) {
      toast.error("Couldn't delete.");
    }
  };

  const onSaved = (saved) => {
    if (editing) setUpdates(updates.map((x) => x.id === saved.id ? saved : x));
    else setUpdates([saved, ...updates]);
  };

  return (
    <div className="mt-12" data-testid={readOnly ? "public-updates-section" : "my-updates-section"}>
      <UpdatesHeader readOnly={readOnly} onNew={() => { setEditing(null); setDialogOpen(true); }} />

      {loading ? (
        <p className="mt-6 font-manrope text-ink-soft animate-soft-pulse">Loading…</p>
      ) : updates.length === 0 ? (
        <UpdatesEmptyState readOnly={readOnly} />
      ) : (
        <div className="mt-6 grid gap-4">
          {updates.map((u) => (
            <UpdateItem
              key={u.id}
              update={u}
              readOnly={readOnly}
              onEdit={() => { setEditing(u); setDialogOpen(true); }}
              onDelete={() => remove(u.id)}
            />
          ))}
        </div>
      )}

      {!readOnly && (
        <UpdateDialog
          open={dialogOpen}
          onOpenChange={setDialogOpen}
          existing={editing}
          onSaved={onSaved}
        />
      )}
    </div>
  );
}
